import { Viewport, ISnapZoomOptions } from 'pixi-viewport';
import { Subject } from 'rxjs';
import { delayFor } from 'app/core/utilities/async-utils';

export interface ZoomDirection {
  value: 'in' | 'out' | 'none';
}

export interface ZoomOptions {
  minZoom: number;
  maxZoom: number;
  defaultZoom: number;
  zoomStep: number;
  animationTime?: number;
  animationEase?: string;
  continuousZoomInterval?: number; // Time in ms between zoom steps during continuous zooming
}

/**
 * ZoomController handles zoom functionality for a Viewport
 */
export class ZoomController {
  private viewport: Viewport;
  private zoomTimeout: number | undefined;
  private continuousZoomIntervalId: NodeJS.Timeout | null = null;
  private smoothZoomStep = 0.01;
  public zoomDirection$ = new Subject<ZoomDirection>();
  public zoomComplete$ = new Subject<void>();

  public constructor(
    viewport: Viewport,
    public options: ZoomOptions,
  ) {
    this.viewport = viewport;
    if (!this.options.continuousZoomInterval) {
      this.options.continuousZoomInterval = 20;
    }
  }

  /**
   * Get the current zoom level
   */
  public get currentZoom(): number {
    return this.viewport && this.viewport.scale && this.viewport.scale.x
      ? this.viewport.scale.x
      : this.options.defaultZoom;
  }

  /**
   * Start continuous zoom (when button is held down)
   * @param zoomIn Whether to zoom in (true) or out (false)
   */
  public startContinuousZoom(zoomIn: boolean): void {
    // First immediate zoom action
    if (zoomIn) {
      if (this.currentZoom < this.options.maxZoom) {
        void this.smoothZoomTo(this.currentZoom + this.smoothZoomStep);
      }
    } else {
      if (this.currentZoom > this.options.minZoom) {
        void this.smoothZoomTo(this.currentZoom - this.smoothZoomStep);
      }
    }

    // Stop any existing interval
    this.stopContinuousZoom();

    // Set direction indicator for UI
    this.zoomDirection$.next({ value: zoomIn ? 'in' : 'out' });

    // Set up continuous zooming with small increments for smoothness
    this.continuousZoomIntervalId = setInterval(() => {
      if (zoomIn) {
        if (this.currentZoom < this.options.maxZoom) {
          void this.smoothZoomTo(this.currentZoom + this.smoothZoomStep);
        } else {
          this.stopContinuousZoom();
        }
      } else {
        // Check if we're at or very close to the minimum zoom
        if (this.currentZoom > this.options.minZoom + 0.001) {
          void this.smoothZoomTo(this.currentZoom - this.smoothZoomStep);
        } else {
          // We're at the minimum zoom, stop continuous zooming
          this.stopContinuousZoom();
        }
      }
    }, this.options.continuousZoomInterval);
  }

  /**
   * Stop continuous zoom (when button is released)
   */
  public stopContinuousZoom(): void {
    if (this.continuousZoomIntervalId) {
      clearInterval(this.continuousZoomIntervalId);
      this.continuousZoomIntervalId = null;

      // Check if we're at boundaries and emit appropriate notifications
      const epsilon = 0.001;
      if (Math.abs(this.currentZoom - this.options.minZoom) < epsilon) {
        // We're at min zoom - notify UI
        this.zoomDirection$.next({ value: 'none' });
      } else if (Math.abs(this.currentZoom - this.options.maxZoom) < epsilon) {
        // We're at max zoom - notify UI
        this.zoomDirection$.next({ value: 'none' });
      } else {
        // Normal stop
        this.zoomDirection$.next({ value: 'none' });
      }

      // Emit zoom complete event for bounds checking
      this.zoomComplete$.next();
    }
  }

  /**
   * Cancel any in-progress zoom animations
   */
  public cancelZoomAnimations(): void {
    // Clear any continuous zoom
    this.stopContinuousZoom();

    // Remove any snap-zoom plugin that might be active
    if (this.viewport.plugins.get('snap-zoom')) {
      this.viewport.plugins.remove('snap-zoom');
    }

    // Reset zoom direction indication
    this.zoomDirection$.next({ value: 'none' });
  }

  /**
   * Perform a smooth zoom without animation delay for continuous zooming
   */
  private smoothZoomTo(zoomLevel: number): void {
    // Clamp zoom level to min/max
    const clampedZoom = Math.min(Math.max(zoomLevel, this.options.minZoom), this.options.maxZoom);

    // Direct zoom for smoothness - no animation
    this.viewport.setZoom(clampedZoom, true);

    // Emit zoom complete event for bounds checking
    this.zoomComplete$.next();
  }

  /**
   * Zoom to a specific level
   */
  public async zoomTo(zoomLevel: number, options?: ISnapZoomOptions): Promise<void> {
    // Round to 2 decimal places for precision
    const zoom = Math.floor(zoomLevel * 100) / 100;

    // Clamp zoom level to min/max
    const clampedZoom = Math.min(Math.max(zoom, this.options.minZoom), this.options.maxZoom);

    if (options) {
      // recalculate width from percentage for snapZoom
      options.width = this.viewport.screenWidth / clampedZoom;
      this.viewport.snapZoom(options);

      // Update zoom direction to update UI
      this.zoomDirection$.next({ value: clampedZoom > this.currentZoom ? 'in' : 'out' });

      // Clear any existing timeout and set a new one
      window.clearTimeout(this.zoomTimeout);
      this.zoomTimeout = window.setTimeout(() => {
        this.zoomDirection$.next({ value: 'none' });
      }, options.time || 500);
    } else {
      // Immediate zoom without animation
      this.viewport.setZoom(clampedZoom, true);
      this.zoomDirection$.next({ value: 'none' });
    }

    // Ensure cleanup happens after zoom is complete
    if (options && options.time) {
      await delayFor(options.time);
    } else {
      await delayFor(100);
    }

    // Emit zoom complete event for bounds checking
    this.zoomComplete$.next();
  }

  /**
   * Reset zoom to default level
   */
  public async resetZoom(): Promise<void> {
    await this.zoomTo(this.options.defaultZoom, this.getAnimationOptions());
  }

  /**
   * Get standard animation options
   */
  private getAnimationOptions(): ISnapZoomOptions {
    return {
      time: this.options.animationTime || 500,
      removeOnComplete: true,
      removeOnInterrupt: true,
      ease: this.options.animationEase || 'easeInOutSine',
    };
  }
}
